import React, { Ref, ChangeEvent, ReactNode } from 'react';
import { ValidationOptions, ErrorMessage, FieldErrors } from 'react-hook-form';
import styled from 'styled-components/macro';

export interface ISelectInputOption {
  label: string;
  value: any; // eslint-disable-line @typescript-eslint/no-explicit-any
}

interface ISelectInputProps {
  name: string;
  label: string | (() => ReactNode);
  options?: ISelectInputOption[];
  value?: string;
  disabled?: boolean;
  validation?: ValidationOptions;
  errors?: FieldErrors<Record<string, unknown>>;
  autoFocus?: boolean;
  children?: ReactNode;
  register?: (ref: Element, validationOptions?: ValidationOptions) => Ref<HTMLSelectElement>;
  onChange?: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLSelectElement>) => void;
}

const SelectInput = ({
  name,
  label,
  options,
  value,
  disabled,
  validation,
  errors,
  autoFocus,
  register,
  onChange,
  onBlur,
  children,
  ...rest
}: ISelectInputProps): JSX.Element | null => {
  const isValid = !(errors && errors[name]);
  return (
    <Label>
      {typeof label === 'function' ? (
        label()
      ) : label ? (
        <LabelText required={!!validation?.required}>{label}</LabelText>
      ) : null}
      {children ||
        (options && (
          <>
            <Select
              name={name}
              ref={(element: HTMLSelectElement): void => {
                if (register) {
                  register(element, validation);
                }
              }}
              value={value}
              className={isValid ? '' : 'invalid'}
              disabled={disabled}
              autoFocus={autoFocus}
              autoComplete="off"
              aria-label={name}
              onChange={onChange}
              onBlur={onBlur}
              {...rest}
            >
              {options.map(({ label, value }) => (
                <option value={value} key={`${label}-${value}`}>
                  {label}
                </option>
              ))}
            </Select>
            {!isValid && (
              <ErrorMessageContainer>
                <ErrorMessage name={name} errors={errors} />
              </ErrorMessageContainer>
            )}
          </>
        ))}
    </Label>
  );
};

export const Label = styled.label`
  display: flex;
  flex-direction: column;
  font-size: 14px;
`;

interface ILabelTextProps {
  required?: boolean;
}

export const LabelText = styled.div<ILabelTextProps>`
  margin-bottom: 10px;

  font-weight: 600;

  &[required]:after {
    content: '*';
    color: var(--chakra-colors-text-danger);
    margin-left: 2px;
  }
`;

export const Select = styled.select`
  height: 38px;
  width: 100%;
  border-radius: 7px;
  border: 1px solid hsl(0, 0%, 82%);
  padding: 9px 12px;
  font-size: 14px;
  background-color: var(--chakra-colors-background-default);
  color: hsl(235, 81%, 13%);
  transition: border-color 0.1s ease-in-out;
  appearance: none;
  background: url(data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI4IiBoZWlnaHQ9IjUiIHZpZXdCb3g9IjAgMCA4IDUiPgogICAgPGcgZmlsbD0ibm9uZSIgZmlsbC1ydWxlPSJldmVub2RkIj4KICAgICAgICA8ZyBmaWxsPSIjMDUwRjVGIiBmaWxsLXJ1bGU9Im5vbnplcm8iPgogICAgICAgICAgICA8cGF0aCBkPSJNMTgwMyAyMjQuMDc1bC0yLjcxNC0yLjg0NGMtLjI5NC0uMzA4LS43NzEtLjMwOC0xLjA2NSAwLS4yOTUuMzA4LS4yOTUuODA4IDAgMS4xMTdsMy4yNzcgMy40MzRjLjI3OC4yOS43MjcuMjkgMS4wMDQgMGwzLjI3Ny0zLjQzNGMuMjk1LS4zMDkuMjk1LS44MDkgMC0xLjExNy0uMjk0LS4zMDgtLjc3LS4zMDgtMS4wNjUgMGwtMi43MTQgMi44NDR6IiB0cmFuc2Zvcm09InRyYW5zbGF0ZSgtMTc5OSAtMjIxKSIvPgogICAgICAgIDwvZz4KICAgIDwvZz4KPC9zdmc+Cg==)
    no-repeat calc(100% - 10px) 50%;
  background-color: var(--chakra-colors-background-default);
  padding-right: 28px;
  cursor: pointer;

  &:hover:not([disabled]) {
    border-color: hsl(0, 0%, 70%);
  }

  &.invalid:not([disabled]) {
    border-color: #da4167;
  }

  &:focus:not([disabled]) {
    border-color: var(--chakra-colors-text-brand);
  }

  &[disabled] {
    background: #f3f3f3;
    opacity: 0.8;
  }
`;

const ErrorMessageContainer = styled.div`
  margin-top: 8px;
  color: var(--chakra-colors-text-danger);
`;

export default SelectInput;
